/**
 * BottomTabBar — Plan | Trips | Account.
 *
 * Only rendered on the two surfaces the FLOW spec allows: HomePlan
 * ("/") and Trips ("/trips"). NOT rendered on Welcome, Login,
 * Onboarding, Book, Your Week, Pay, or the Account sheet. If you find
 * yourself importing this into another rider page — don't. Add the
 * tab bar there via router-level composition instead.
 *
 * Icons are lucide-react. Active state uses orange (--dusk-cta); no
 * blue is introduced (per token rules).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Car, User } from "lucide-react";

const TABS = [
  { id: "plan",    label: "Plan",    icon: CalendarDays, to: "/" },
  { id: "trips",   label: "Trips",   icon: Car,          to: "/trips" },
  { id: "account", label: "Account", icon: User,         to: "/account" },
];

export default function BottomTabBar({ active = "plan" }) {
  const nav = useNavigate();
  return (
    <nav
      style={{
        position: "fixed",
        left: 0, right: 0, bottom: 0,
        paddingBottom: "max(env(safe-area-inset-bottom, 0px), 12px)",
        paddingTop: 8,
        background: "rgba(10,17,40,0.85)",
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "flex-start",
        zIndex: 40,
      }}
      data-testid="rider-tab-bar"
    >
      {TABS.map(({ id, label, icon: Icon, to }) => {
        const isActive = id === active;
        const color = isActive ? "var(--dusk-cta)" : "var(--dusk-text-muted)";
        return (
          <button
            key={id}
            type="button"
            onClick={() => nav(to)}
            className="flex flex-col items-center gap-1"
            style={{
              flex: 1, padding: "6px 4px",
              background: "transparent", border: "none",
            }}
            data-testid={`tab-${id}`}
            aria-current={isActive ? "page" : undefined}
          >
            <Icon size={22} color={color} strokeWidth={2} />
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 600, fontSize: 11, letterSpacing: "0.02em",
                color,
              }}
            >
              {label}
            </span>
            {isActive && (
              <span
                aria-hidden="true"
                style={{
                  marginTop: 2,
                  height: 2, width: 22, borderRadius: 2,
                  background: "var(--dusk-cta)",
                }}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}
